/* UTILS */
import router from "@/router"
import { storeToRefs } from "pinia"
import { setup, fromPromise, createActor, assign } from "xstate"

/* STORES */
import { useConfigStore } from "@/stores/config"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import { useLeaveAppModalStore } from "@/components/af/LeaveAppModal/leaveAppModal.store"

/* HELPERS */
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useCookies } from "@vueuse/integrations/useCookies"
import { getFeatureFlags } from "@/api/viktor/feature-flags/feature-flags"
import { getPendingInvitationRedirect } from "@/api/viktor/me/invitations"

import { swipeMachine } from "@/features/swipe/machines/swipeMachine"
import { driveMachine } from "@/features/drive/machines/driveMachine"
import { brainMachine } from "@/features/brain/machines/brainMachine"
import { screeningMachine } from "@/features/screening/machines/screeningMachine"
import { meetingMachine } from "@/features/book-meeting/machine/meeting.machine"
import { screeningCvMachine } from "@/features/screening-cv/machines/screeningCvMachine"

/* TYPES */
import type { GlobalMachineEventType, GuardsType } from "./types"
import { COOKIES } from "@/config/constants"

const { getSessionItem, setSessionItem } = useSessionStorage()

const originalSearchParams = new URLSearchParams(window.location.search)
const iframe_token =
  originalSearchParams.get("iframe_token") || getSessionItem("iframe_token")
const target_uuid =
  originalSearchParams.get("target_uuid") || getSessionItem("target_uuid")
const sharepool_uuid =
  originalSearchParams.get("sharepool_uuid") || getSessionItem("sharepool_uuid")
// Shape has been replaced by Swipe: legacy /shape URLs and ?goto=shape must land on swipe.
// The router can't handle it alone since its first navigation always goes to "/"
const isLegacyShapePath = /^\/shape(\/|$)/.test(window.location.pathname)
const raw_goto_param =
  originalSearchParams.get("goto") ||
  (isLegacyShapePath ? "swipe" : null) ||
  getSessionItem("goto_param")
const goto_param =
  raw_goto_param?.toLowerCase() === "shape" ? "swipe" : raw_goto_param
const redirect_url =
  originalSearchParams.get("redirect_url") || getSessionItem("redirect_url")
const version = originalSearchParams.get("version") || getSessionItem("version")

// Loader for user data
// this is used in the machine loading state
// return the next assessment key
const loadUserDatas = fromPromise(async () => {
  const {
    fetchUser,
    fetchWorkflows,
    fetchNextAssessment,
    fetchAssessmentSummary,
    fetchPoleEmploiSynthesis,
    fetchSharepools,
    isAdminOnly,
    isRecruiter,
    isManager,
  } = useUserStore()
  const { fetchLocales, changeAppLocale } = useI18nStore()
  const configStore = useConfigStore()
  const { isActive } = storeToRefs(useLeaveAppModalStore())
  const {
    isAllWorkflowsDone,
    isFromInt,
    isGuestAcc,
    isPendingUser,
    nextAssessmentName,
  } = storeToRefs(useUserStore())

  await fetchLocales()
  const user = await fetchUser()

  // A pending user (cst_na) has not accepted the terms of use yet:
  // so we only run the calls he is allowed to make
  const canFetchUserDatas = !isPendingUser.value

  // If the user has pending invitations, redirect to the auth portal so they
  // can accept/refuse them first (role invites -> /invitations/role,
  // candidate invites -> /invitations/share-profile).
  // Skipped for integration and guest contexts, which don't use the auth portal.
  // `user` is falsy when fetchUser already redirected (401 -> login, MFA,
  // terms-of-use), so those redirects keep priority over invitations.
  if (user && !isFromInt.value && !isGuestAcc.value && canFetchUserDatas) {
    const invitationRedirect = await getPendingInvitationRedirect()
    if (invitationRedirect) {
      window.location.href = `${configStore.urls.auth_url}${invitationRedirect}`
      // The full-page navigation above is async: the JS keeps running. Resolving
      // here would fire the machine's onDone with an empty output, no guard would
      // match and we would fall through to the default "stepper" target — the
      // stepper flashes on screen before the browser actually leaves.
      // Never resolving keeps the machine in "loading" (LoadingView) until then.
      return new Promise<never>(() => {})
    }
  }

  let workflows = []
  let sharepools = null

  if (canFetchUserDatas) {
    // Fetch assessments
    await fetchAssessmentSummary()
    if (user.is_pole_emploi) {
      await fetchPoleEmploiSynthesis()
    }

    // Fetch workflows
    workflows = await fetchWorkflows()
    sharepools = await fetchSharepools()
  }

  // change AppLocale, in case of cookie locale is not the /user locale
  await changeAppLocale(user.user.locale_id)

  // if no next assessment and not isPoleEmploi, redirect to workspace, depends on the user permissions
  // I check if it's Pole Emploi user, because in retake test mode, the getNextAssessmentKey can be null and he could have a test to do...

  // get goto in globalmachine context
  if (canFetchUserDatas && !goto_param && isAllWorkflowsDone.value === true) {
    const afAuthSession = useCookies().get(COOKIES.AUTH_SESSION)
    const from_login = afAuthSession?.from_login
    if (isFromInt.value) {
      // If guest, send to redirect or thank you page
      if (redirect_url) {
        window.location.href = redirect_url
        return
      }

      globalActor.send({ type: "GLOBAL.GUEST.END" })
    } else if (isAdminOnly) {
      // If admin only
      window.location.href = configStore.urls.adminAccount
    } else if (isRecruiter) {
      // If recruiter
      window.location.href = configStore.urls.recruiterAccount
    } else if (isManager) {
      // If manager
      window.location.href = configStore.urls.managerAccount
    } else {
      // Else, personal
      if (sharepools?.body && sharepools.body.length > 0 && !from_login) {
        isActive.value = false
        globalActor.send({ type: "GLOBAL.GO.STEPPER" })
      } else {
        // window.location.href = configStore.urls.personalAccount
      }
    }
  }

  configStore.setAppIsReady()

  // Check if assessments are blocked
  const ffBlockAssessments = await getFeatureFlags("block-assessments")

  let nextAssessment = null
  const goToNext = useCookies().get(COOKIES.GOTO_NEXT)
  if (canFetchUserDatas && goToNext == 1) {
    // get workflow with status not completed
    const workflowsToPass = workflows.filter(
      (workflow) => workflow.status !== "completed"
    )
    if (workflowsToPass.length == 1) {
      const workflow = workflowsToPass[0]
      const workflow_uuid = workflow.uuid

      await fetchNextAssessment(workflow_uuid ?? null)
      nextAssessment = nextAssessmentName
    }

    useCookies().remove(COOKIES.GOTO_NEXT, COOKIES.OPTIONS)
  }

  return {
    nextAssessment: nextAssessment,
    canAccessTests: !ffBlockAssessments.is_active,
  }
})

// The screening flows have their own entry point: they don't go through the
// stepper, so they are routed to their machine as soon as the user datas are
// loaded.
const isScreeningFlow = ({ context }) => {
  return (
    context.goto?.startsWith("screening") ||
    (getSessionItem("origin") as string)?.startsWith("screening")
  )
}

const loadAssessments = fromPromise(async () => {
  const { fetchAssessmentSummary } = useUserStore()
  await fetchAssessmentSummary()
})

// Loader for route, to directly go to a specific step
// e.g. /?goto=swipe will directly go to the swipe step
// this is used in the machine entry state
const loadRoute = () => {
  if (goto_param !== "voice") {
    return goto_param
  } else {
    // If user was redirected to voice keep value in session
    const wasRedirectedToVoice =
      goto_param === "voice" || getSessionItem("wasRedirectedToVoice") === true

    setSessionItem("wasRedirectedToVoice", wasRedirectedToVoice)
  }

  return null
}

const assignIframeToken = fromPromise(async () => {
  if (version) {
    setSessionItem("version", version)
  }
  if (iframe_token) {
    setSessionItem("iframe_token", iframe_token)
  }
  if (target_uuid) {
    setSessionItem("target_uuid", target_uuid)
  }
  if (sharepool_uuid) {
    setSessionItem("sharepool_uuid", sharepool_uuid)
  }
  if (goto_param) {
    setSessionItem("goto_param", goto_param)

    // We want to persist this goto param to be able to redirect the user to the same page after a page reload
    // can be screening or screening-cv
    if (goto_param.startsWith("screening")) {
      setSessionItem("origin", goto_param)
    }
  }
  if (redirect_url) {
    setSessionItem("redirect_url", redirect_url)
  }

  return iframe_token
})

// Dev mode
const setDevMode = () => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const isDevMode = urlParams.get("dev-menu") === "1"
  const isRightHost = window.location.hostname !== "app.assessfirst.com"

  if (isDevMode && isRightHost) localStorage.setItem("dev-mode", "1")

  return (isDevMode && isRightHost) || null
}

// Exit app
// this is used in the machine exit state to redirect to workspace, depends on the user
const exitApp = fromPromise(async () => {
  const { urls } = useConfigStore()
  const { isAdminOnly, isRecruiter, isManager, sharepools } = useUserStore()
  const { isActive } = storeToRefs(useLeaveAppModalStore())

  if (isAdminOnly) {
    // If admin only
    window.location.href = urls.adminAccount
  } else if (isRecruiter) {
    // If recruiter
    window.location.href = urls.recruiterAccount
  } else if (isManager) {
    // If manager
    window.location.href = urls.managerAccount
  } else {
    // Else, personal
    if (sharepools.body && sharepools.body.length > 0) {
      isActive.value = false
      globalActor.send({ type: "GLOBAL.STEPPER.TRANSITION" })
    } else {
      window.location.href = urls.personalAccount
    }
  }
})

/**
 * Global machine
 * This machine is the main machine of the app
 * flow:
 * - entry: loadRoute update the goto context, and redirect to the loading state
 * - entry: loadInContext for Luciano
 * - entry: setDevMode to see the dev mode buttons
 * - state loading: loadUserDatas onDone redirect to the stepper or the swipe, drive or brain state depending on the goto context
 * - state loadStepper: each test redirect to this state, and this state update assessments
 * - state stepper: stepper
 * - states swipe, drive, brain: child machines
 * - state exit: exit app
 **/
export const globalMachine = setup({
  actors: {
    loadUserDatas,
    loadAssessments,
    exitApp,
    swipeMachine,
    driveMachine,
    brainMachine,
    screeningMachine,
    screeningCvMachine,
    meetingMachine,
    assignIframeToken,
  },
  types: {} as {
    context: {
      goto: string | null
      devMode: boolean | null
      next: string | null
      iframe_token: string | null
      nextAssessment: string | null
      canAccessTests: boolean
    }
    events: GlobalMachineEventType
    guards: GuardsType
  },
  actions: { loadRoute, setDevMode },
}).createMachine({
  id: "globalMachine",
  context: {
    goto: null,
    devMode: null,
    next: null,
    iframe_token: null,
    nextAssessment: null,
    canAccessTests: true,
  },
  initial: "init",
  entry: assign({
    goto: loadRoute,
    devMode: setDevMode,
  }),
  on: {
    "GLOBAL.DEV": {
      actions: [() => router.replace({ name: "dev" })],
    },
  },
  states: {
    init: {
      invoke: {
        id: "assignIframeToken",
        src: "assignIframeToken",
        onDone: [
          {
            guard: isScreeningFlow,
            target: "loadScreening",
            actions: assign({
              iframe_token: ({ event }) => event.output,
            }),
          },
          {
            actions: assign({
              iframe_token: ({ event }) => event.output,
            }),
            target: "loading",
          },
        ],
      },
    },
    // loading for app needs
    loading: {
      entry: [() => router.push({ name: "loading" })],
      on: {
        "GLOBAL.GUEST.END": {
          target: "thanksATS",
        },
        "GLOBAL.STEPPER.TRANSITION": {
          target: "transition",
        },
        "GLOBAL.GO.STEPPER": {
          target: "stepper",
        },
        "GLOBAL.GUEST.CONTACTSUPPORT": {
          target: "contactSupport",
        },
      },
      invoke: {
        id: "loadUser",
        src: "loadUserDatas",
        onDone: [
          {
            guard: ({ context, event }) =>
              "goto" in context &&
              context.goto !== null &&
              !event.output.canAccessTests,
            target: "maintenance",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "swipe" ||
              event.output?.nextAssessment?.value === "swipe",
            target: "swipe",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "drive" ||
              event.output?.nextAssessment?.value === "drive",
            target: "drive",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "brain" ||
              event.output?.nextAssessment?.value === "brain",
            target: "brain",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "voice" ||
              event.output?.nextAssessment?.value === "voice",
            target: "voice",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "screening_cv" ||
              event.output?.nextAssessment?.value === "screening-cv",
            target: "screeningCv",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "screening_interview" ||
              event.output?.nextAssessment?.value === "screening",
            target: "screening",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "meeting" ||
              event.output?.nextAssessment?.value === "meeting",
            target: "meeting",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "move" ||
              event.output?.nextAssessment?.value === "move",
            target: "move",
          },
          {
            guard: ({ context, event }) =>
              context.goto === "movebis" ||
              event.output?.nextAssessment?.value === "movebis",
            target: "movebis",
          },
          {
            guard: ({ context }) => context.goto === "transition",
            target: "transition",
          },
          {
            guard: ({ context }) => context.goto === "contact-support",
            target: "contactSupport",
          },
          {
            target: "stepper",
            actions: assign({
              next: ({ event }) => event.output?.nextAssessment ?? null,
              canAccessTests: ({ event }) =>
                event.output?.canAccessTests ?? true,
            }),
          },
        ],
      },
    },
    loadStepper: {
      invoke: {
        id: "loadStepper",
        src: "loadAssessments",
        onDone: {
          target: "stepper",
        },
      },
    },
    loadScreening: {
      entry: [() => router.push({ name: "loading" })],
      invoke: {
        id: "loadScreeningUser",
        src: "loadUserDatas",
        onDone: [
          {
            guard: ({ context }) =>
              (context.goto && context.goto === "screening_cv") ||
              getSessionItem("origin") === "screening_cv",
            target: "screeningCv",
          },
          {
            target: "screening",
          },
        ],
      },
    },
    stepper: {
      entry: [() => router.replace({ name: "stepper" })],
      on: {
        "GLOBAL.STEPPER.EXIT": {
          target: "exit",
        },
        "GLOBAL.STEPPER.NEXT": [
          {
            guard: ({ context }) =>
              "next" in context &&
              context.next !== null &&
              !context.canAccessTests,
            target: "maintenance",
          },
          {
            guard: ({ context }) => context.next === "swipe",
            target: "swipe",
          },
          {
            guard: ({ context }) => context.next === "drive",
            target: "drive",
          },
          {
            guard: ({ context }) => context.next === "brain",
            target: "brain",
          },
          {
            guard: ({ context }) => context.next === "voice",
            target: "voice",
          },
          {
            guard: ({ context }) => context.next === "screening",
            target: "screening",
          },
          {
            guard: ({ context }) => context.next === "meeting",
            target: "meeting",
          },
        ],
        "GLOBAL.STEPPER.SWIPE": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "swipe",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.DRIVE": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "drive",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.BRAIN": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "brain",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.VOICE": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "voice",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.SCREENING_INTERVIEW": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "screening",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.SCREENING_CV": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "screeningCv",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.MEETING": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "meeting",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.MOVE": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "move",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.MOVEBIS": [
          {
            guard: ({ context }) => context.canAccessTests,
            target: "movebis",
          },
          {
            target: "maintenance",
          },
        ],
        "GLOBAL.STEPPER.TRANSITION": {
          target: "transition",
        },
        "GLOBAL.GUEST.CONTACTSUPPORT": {
          target: "contactSupport",
        },
        "GLOBAL.GUEST.MAINTENANCE": {
          target: "maintenance",
        },
      },
    },
    swipe: {
      entry: [() => router.replace({ name: "swipe" })],
      on: {
        "GLOBAL.SWIPE.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.DRIVE": {
          target: "drive",
        },
        "GLOBAL.REDIRECT.BRAIN": {
          target: "brain",
        },
        "GLOBAL.REDIRECT.VOICE": {
          target: "voice",
        },
        "GLOBAL.REDIRECT.MOVE": {
          target: "move",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
        "GLOBAL.GUEST.CONTACTSUPPORT": {
          target: "contactSupport",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    drive: {
      entry: [() => router.replace({ name: "drive" })],
      on: {
        "GLOBAL.DRIVE.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.SWIPE": {
          target: "swipe",
        },
        "GLOBAL.REDIRECT.BRAIN": {
          target: "brain",
        },
        "GLOBAL.REDIRECT.VOICE": {
          target: "voice",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
        "GLOBAL.GUEST.CONTACTSUPPORT": {
          target: "contactSupport",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    brain: {
      entry: [() => router.replace({ name: "brain" })],
      on: {
        "GLOBAL.BRAIN.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.SWIPE": {
          target: "swipe",
        },
        "GLOBAL.REDIRECT.DRIVE": {
          target: "drive",
        },
        "GLOBAL.REDIRECT.VOICE": {
          target: "voice",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
        "GLOBAL.GUEST.CONTACTSUPPORT": {
          target: "contactSupport",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    voice: {
      entry: [() => router.replace({ name: "voice" })],
      on: {
        "GLOBAL.VOICE.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.SWIPE": {
          target: "swipe",
        },
        "GLOBAL.REDIRECT.DRIVE": {
          target: "drive",
        },
        "GLOBAL.REDIRECT.BRAIN": {
          target: "brain",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
        "GLOBAL.GUEST.CONTACTSUPPORT": {
          target: "contactSupport",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    screeningCv: {
      entry: [
        () => {
          router.replace({ name: "screeningCv" })
        },
      ],
      on: {
        "GLOBAL.SCREENING_CV.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.SCREENING_INTERVIEW": {
          target: "screening",
        },
        "GLOBAL.REDIRECT.SWIPE": {
          target: "swipe",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
        "GLOBAL.REDIRECT.DRIVE": {
          target: "drive",
        },
        "GLOBAL.REDIRECT.BRAIN": {
          target: "brain",
        },
        "GLOBAL.REDIRECT.VOICE": {
          target: "voice",
        },
      },
      onDone: { target: "stepper" },
    },
    screening: {
      entry: [() => router.replace({ name: "screening" })],
      on: {
        "GLOBAL.SCREENING.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.SWIPE": {
          target: "swipe",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
        "GLOBAL.REDIRECT.DRIVE": {
          target: "drive",
        },
        "GLOBAL.REDIRECT.BRAIN": {
          target: "brain",
        },
        "GLOBAL.REDIRECT.VOICE": {
          target: "voice",
        },
        "GLOBAL.SCREENING.RESELLER.DONE": {
          target: "thanksATS",
        },
        "GLOBAL.REDIRECT.SCREENING_CV": {
          target: "screeningCv",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    meeting: {
      entry: [() => router.replace({ name: "meeting" })],
      on: {
        "GLOBAL.MEETING.DONE": {
          target: "loadStepper",
        },
      },
    },
    move: {
      entry: [() => router.replace({ name: "move" })],
      on: {
        "GLOBAL.MOVE.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.MOVEBIS": {
          target: "movebis",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    movebis: {
      entry: [() => router.replace({ name: "movebis" })],
      on: {
        "GLOBAL.MOVEBIS.DONE": {
          target: "loadStepper",
        },
        "GLOBAL.REDIRECT.MEETING": {
          target: "meeting",
        },
      },
      onDone: {
        target: "stepper",
      },
    },
    transition: {
      entry: [() => router.replace({ name: "transition" })],
    },
    maintenance: {
      entry: [() => router.replace({ name: "maintenance" })],
    },
    contactSupport: {
      entry: [() => router.replace({ name: "contactSupport" })],
      on: {
        "GLOBAL.GUEST.STEPPER": {
          target: "stepper",
        },
      },
    },
    thanksATS: {
      entry: [() => router.replace({ name: "thanksATS" })],
    },
    exit: {
      on: {
        "GLOBAL.STEPPER.TRANSITION": {
          target: "transition",
        },
      },
      invoke: {
        src: "exitApp",
      },
    },
    final: {
      type: "final",
      invoke: {
        src: "exitApp",
      },
    },
  },
})

export const globalActor = createActor(globalMachine)
